export type GabaritoImage = {
  id: string;
  title: string;
  fileName: string;
  originalFileName: string;
  keyword: string;
  src: string;
};

export type GabaritoState = {
  images: GabaritoImage[];
  postKeywords: string;
  commentKeywords: string;
  postNotes: string;
  commentNotes: string;
};

export const initialGabaritoState: GabaritoState = {
  images: [],
  postKeywords: "",
  commentKeywords: "",
  postNotes: "",
  commentNotes: "",
};

export const splitKeywords = (value: string) =>
  value
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);

/** Separa um texto colado em palavras: vírgula, ponto e vírgula ou quebra de linha. */
export const separarPalavras = (texto: string) =>
  texto
    .split(/[,;\r\n]+/)
    .map((palavra) => palavra.trim())
    .filter(Boolean);

export const appendKeywords = (currentValue: string, keywords: string[]) => {
  const existing = splitKeywords(currentValue);
  const unique = [...existing];

  keywords.forEach((keyword) => {
    const normalizedKeyword = keyword.trim();
    if (
      normalizedKeyword &&
      !unique.some(
        (item) => item.toLowerCase() === normalizedKeyword.toLowerCase(),
      )
    ) {
      unique.push(normalizedKeyword);
    }
  });

  return unique.join(", ");
};

export const removeKeyword = (currentValue: string, keyword: string) =>
  splitKeywords(currentValue)
    .filter((item) => item.toLowerCase() !== keyword.trim().toLowerCase())
    .join(", ");

export const sanitizeKeywordForFileName = (keyword: string) => {
  const sanitized = keyword
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return sanitized || "imagem";
};

export const pickRandomKeyword = (
  keywords: string[],
  random: () => number = Math.random,
) => {
  const validKeywords = keywords.map((keyword) => keyword.trim()).filter(Boolean);
  if (validKeywords.length === 0) return "imagem";

  const index = Math.min(
    validKeywords.length - 1,
    Math.floor(random() * validKeywords.length),
  );

  return validKeywords[index];
};

export const renameFileWithRandomKeyword = (
  file: File,
  keywords: string[],
  random: () => number = Math.random,
) => {
  const keyword = pickRandomKeyword(keywords, random);
  const extension = file.name.match(/\.[^.]+$/)?.[0] ?? "";
  const fileName = `${sanitizeKeywordForFileName(keyword)}${extension}`;

  return {
    file: new File([file], fileName, {
      type: file.type,
      lastModified: file.lastModified,
    }),
    keyword,
  };
};

export const getGabaritoStorageKey = (profileId: string) =>
  `georanking:gabarito:${profileId}`;

export const readGabaritoState = (profileId: string): GabaritoState => {
  try {
    const stored = localStorage.getItem(getGabaritoStorageKey(profileId));
    return stored
      ? { ...initialGabaritoState, ...JSON.parse(stored) }
      : initialGabaritoState;
  } catch {
    return initialGabaritoState;
  }
};

export const writeGabaritoState = (
  profileId: string,
  gabarito: GabaritoState,
) => {
  localStorage.setItem(getGabaritoStorageKey(profileId), JSON.stringify(gabarito));
};

export const mergeGabaritoComPublicacao = (
  remote: Partial<GabaritoState> | null | undefined,
  local: GabaritoState,
  publicadas: string[],
): GabaritoState => {
  const base: GabaritoState = {
    ...local,
    postKeywords: appendKeywords(
      remote?.postKeywords || "",
      splitKeywords(local.postKeywords),
    ),
    commentKeywords: appendKeywords(
      remote?.commentKeywords || "",
      splitKeywords(local.commentKeywords),
    ),
    postNotes: local.postNotes || remote?.postNotes || "",
    commentNotes: local.commentNotes || remote?.commentNotes || "",
  };
  return {
    ...base,
    postKeywords: appendKeywords(base.postKeywords, publicadas),
  };
};
